import { sumBy, uniq } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    Alert,
    AlertHandle,
    autoinject,
    bind,
    inject,
    Loader,
    Modal,
    ModalHandle,
    Optional,
    Nullable,
    uuidv4generate,
    HelpTooltip,
    Enums,
    slugCase,
    displayFormatTime,
    formatDuration,
    getDurationToNow,
    Duration,
} from 'Mri.Web.Common';
import * as React from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledButtonDropdown } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { FavoriteButton } from '../Core/FavoriteButton';
import { ActiveDisplay } from '../Displays/ActiveDisplay';
import { DisplayEventGroup, DisplayEventsList } from '../Displays/DisplayEventsList';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { DisplayEventProfileViews } from '../DisplayEventProfiles/DisplayEventProfilesList';
import { DisplayEventProfileStore } from '../DisplayEventProfiles/DisplayEventProfileStore';
import { BsCalendar } from '@react-icons/all-files/bs/BsCalendar';
import LinkContainer from 'react-router-bootstrap/lib/LinkContainer';

export interface IDisplayEventModalProps {
    readonly displaySerialNumber: string;
    readonly eventGuid: string;
    readonly modalHandle: ModalHandle;
}

@autoinject
@observer
export class DisplayEventModal extends React.Component<IDisplayEventModalProps> {
    public render() {
        const startTime = this.displayEvent?.downtimeIncidentStartTime ?? this.displayEvent?.creationTime;
        const duration =
            this.displayEvent?.downtimeIncidentDuration != null
                ? new Duration(this.displayEvent?.downtimeIncidentDuration * 1000)
                : this.displayEvent?.downtimeActiveIncident
                    ? getDurationToNow(startTime || new Date())
                    : undefined;
        return (
            <Modal
                title={this.title}
                modalHandle={this.props.modalHandle}
                footerClass="d-none"
                size="xs"
                className="my-2"
                bodyClass="vh-50"
                onOpened={this.loadAsync}
            >

                <div className="d-flex align-items-stretch">
                    {this.displayEvent &&
                        <div className="d-inline-block w-100">
                            <div>
                                <HelpTooltip
                                    subjects={['Downtime Incidents']}
                                    name={slugCase(
                                        Enums.toString(Enums.DowntimeIncidentType, this.displayEvent.downtimeIncidentType)
                                    )}
                                    underline
                                    placement="top"
                                    className={this.eventSeverityStyle(this.displayEvent)}
                                >
                                    <b className={this.eventSeverityStyle(this.displayEvent)}>{this.displayEvent.details}</b>
                                </HelpTooltip>

                                <div className="float-end">
                                    <div className={this.incidentClass(this.displayEvent) + ' me-1'}>
                                        {Enums.toString(Enums.DisplayIncidentLevel, this.displayEvent.incidentLevel)}
                                    </div>

                                    <div className={this.badgeClass(this.displayEvent) + ' me-1'}>
                                        {Enums.toString(Enums.DisplayEventStatus, this.displayEvent.displayEventStatus)}
                                    </div>

                                    {this.displayEvent && <div className="badge bg-warning">Active</div>}

                                    {!this.displayEvent.downtimeActiveIncident && <div className="badge bg-secondary">Inactive</div>}
                                </div>
                            </div>

                            <p className="list-group-item-text mb-0 mt-1 f-14">
                                {Enums.toString(Enums.DowntimeIncidentType, this.displayEvent.downtimeIncidentType)}
                            </p>

                            {duration != null && (
                                <p className="list-group-item-text mb-0">
                                    <b>Duration: </b>
                                    {duration != null ? formatDuration(duration) : '-'}
                                </p>
                            )}

                            {startTime && (
                                <p className="list-group-item-text mb-0">
                                    <b>Started: </b>
                                    {displayFormatTime(startTime, 'EEEE, MMMM do yyyy, h:mm:ss a zzz', this.props.displaySerialNumber)}
                                </p>
                            )}

                        </div>
                    }
                </div>
                <Loader loading={this.loading} showSpinner />
                       
            </Modal>
        );
    }

    @bind
    @action
    private async loadAsync() {
        this.loading = true;
        try {
            await this.displaysStore.loadCurrentDisplayEventsAsync(this.props.displaySerialNumber);
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }


    @bind
    @action
    private badgeClass(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        const badge = 'badge ';
        if (displayEvent.displayEventStatus == Mri.DisplayEvents.DisplayEventStatus.Alert) {
            return badge + 'bg-warning';
        }
        return badge + 'bg-secondary';
    }

    @bind
    @action
    private incidentClass(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        const badge = 'badge ';

        let color = '';

        switch (displayEvent.incidentLevel) {
            default:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentCritical:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentMajor:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentMinor:
                color = 'bg-warning';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentNotification:
                color = 'bg-teal';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentIgnored:
                color = 'bg-secondary';
                break;
        }

        return badge + color;
    }

    @bind
    @action
    private eventSeverityStyle(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        let color = '';
        if (displayEvent.downtimeActiveIncident) {
            switch (displayEvent.incidentLevel) {
                default:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentCritical:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentMajor:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentMinor:
                    color = 'text-warning';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentNotification:
                    color = 'text-teal';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentIgnored:
                    color = 'text-muted';
                    break;
            }
        } else color = 'text-muted';

        return color;
    }

    @computed
    private get displayEvent() {
        var events = this.displaysStore.currentEvents.get(this.props.displaySerialNumber);
        var event: Mri.Web.Internal.Controllers.Displays.DisplayEventModel | undefined;
        if (events) {
            event = events.find(e => e.displayEventGuid == this.props.eventGuid);
        }
        
        return event;
    }


    @computed
    private get title() {
        return (
            <div className="w-100 text-center">
               Event
            </div>
        );
    }

    @observable
    private modalHandle = new ModalHandle();
    @observable
    private alertHandle = new AlertHandle();
    @observable
    private loading = false;
    @observable
    private loadingInfo = false;
    @observable
    private infoError: any | undefined;
       
    @observable
    private checkedDisplayEvents = new Map<string, boolean>();
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private displayEventProfileStore!: DisplayEventProfileStore;
    private createTicketModalHandle = new ModalHandle();
}
